import { matchesHost, satelliteStyle, VECTOR_MAX_ZOOM } from './shared'
import type { BasemapProvider, BasemapStyle, BasemapStyleRequest } from './types'

/*
 * CARTO vector basemaps — wired up, not in use.
 *
 * OpenFreeMap serves the street basemap. This exists as a supervised escape
 * hatch: set VITE_BASEMAP_PROVIDER=carto and redeploy. Keeping it working and
 * tested costs little and means a provider change is a config change.
 *
 * The notes below explain why this is vector rather than the raster service
 * that broke, and why a key alone was never the fix.
 *
 * WHY VECTOR AND NOT RASTER
 * GeoBorder used CARTO's raster PNG tiles (basemaps.cartocdn.com/{dark,light}_all)
 * until CARTO retired the keyless tier. Unauthenticated raster requests now
 * return HTTP 200 with an "API KEY REQUIRED" watermark burned into the image,
 * which is how this surfaced: a defaced but otherwise working map.
 *
 * A key alone would not have been a durable fix. CARTO is retiring the raster
 * service outright and has said it is considering stopping data updates to it,
 * and their guidance is that new applications use the vector basemaps. So this
 * moves to vector, which is:
 *
 *   - the same cartography — Positron and Dark Matter are the styles the raster
 *     tiles were rendered from, so the map looks like it always did, only
 *     sharper, since labels and strokes are drawn client-side;
 *   - not deprecated;
 *   - unwatermarked, and currently served without a key at all.
 *
 * That last point is why this fix could ship before a key was issued.
 *
 * ON THE API KEY
 * The parameter is `key`, NOT `api_key`. This is worth stating plainly because
 * getting it wrong is invisible: CARTO ignores an unrecognised parameter and
 * serves the watermarked tile at HTTP 200, byte-identical to sending no key at
 * all. Verified by hash — `?key=` returns different bytes, `?api_key=` returns
 * exactly the unauthenticated tile.
 *
 * VITE_CARTO_API_KEY is optional and appended by `transformCartoRequest` when
 * set. CARTO's own guidance is that vector does not require a key yet and that
 * they will give notice before it does; their free tier meters 5M tile
 * requests per month across both services. Wiring the key now means that
 * change is a rebuild, not a code change and review.
 *
 * WHY THE KEY CANNOT BE VALIDATED
 * There is no way to detect a bad key from the client. A wrong, empty, or
 * missing key returns HTTP 200 with byte-identical watermarked tiles — no 4xx,
 * no error event, nothing MapLibre can surface. So if this provider is ever
 * switched on, verify the map by looking at it; nothing will report a failure.
 */

const CARTO_STYLE_BASE = 'https://basemaps.cartocdn.com/gl'

/** Positron (light) and Dark Matter (dark) — CARTO's canonical GL styles. */
const CARTO_LIGHT_STYLE = `${CARTO_STYLE_BASE}/positron-gl-style/style.json`
const CARTO_DARK_STYLE = `${CARTO_STYLE_BASE}/dark-matter-gl-style/style.json`

export const CARTO_ATTRIBUTION =
  '<a href="https://carto.com/attributions" target="_blank" rel="noopener">© CARTO</a> · <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">© OpenStreetMap</a>'

/** Registrable domain for CARTO-served assets. Gates key attachment, so it is
 *  matched against the parsed host — never as a substring. */
export const CARTO_HOST_MARKER = 'cartocdn.com'

const apiKey = (import.meta.env.VITE_CARTO_API_KEY ?? '').trim()

/**
 * Appends the CARTO key to CARTO-bound requests. Pass as MapLibre's
 * `transformRequest`; it covers the style, tiles.json, tiles, sprites and
 * glyphs in one place, which a style URL alone cannot do.
 *
 * A no-op when no key is configured, and it never touches non-CARTO URLs, so
 * our own Supabase geometry requests pass through untouched.
 */
export function transformCartoRequest(url: string): { url: string } | undefined {
  if (!apiKey) return undefined
  // Host match, not substring: this decides whether to attach a credential, so
  // `https://evil.example/?x=cartocdn.com` must not qualify.
  if (!matchesHost(url, CARTO_HOST_MARKER)) return undefined
  // Don't double-append if the URL already carries a key.
  if (/[?&]key=/.test(url)) return undefined
  return { url: `${url}${url.includes('?') ? '&' : '?'}key=${encodeURIComponent(apiKey)}` }
}

function getStyle({ theme, kind }: BasemapStyleRequest): BasemapStyle {
  if (kind === 'satellite') return satelliteStyle()
  return {
    style: theme === 'dark' ? CARTO_DARK_STYLE : CARTO_LIGHT_STYLE,
    attribution: CARTO_ATTRIBUTION,
    maxZoom: VECTOR_MAX_ZOOM,
  }
}

export const cartoBasemapProvider: BasemapProvider = {
  name: 'carto-vector+esri',
  getStyle,
}
